/**
 * 求職者タグ付け条件の型定義
 */

import type { AgentApiCustomTagConditionsRecord, SharedCustomField } from '@/types/serializers';

// バックエンド生成型（AgentApiCustomTagConditionsRecord）をベースに、
// 新規作成前（未保存）の条件は id を持たないため id のみ optional 化する。
// 独自 interface を定義せず自動生成型を流用することで as キャストを回避する。
export type TagCondition = Omit<AgentApiCustomTagConditionsRecord, 'id'> & { id?: number };

/** 求職者の実カラムに対する項目選択肢（静的） */
export const CANDIDATE_FIELD_OPTIONS = [
  { label: '氏名', value: 'name' },
  { label: '現職企業名', value: 'company_name' },
  { label: '職種', value: 'job_position' },
  { label: '現在の住所', value: 'current_address' },
  { label: '現年収', value: 'current_salary' },
  { label: '希望年収', value: 'desired_salary' },
];

/**
 * カスタムフィールドの condition_column 値を組み立てる（"field_<id>" 形式）
 * バックエンド（CustomTagCondition）の想定仕様と一致させる
 */
export function buildCustomFieldColumn(fieldId: number): string {
  return `field_${fieldId}`;
}

/**
 * カスタムフィールド一覧から項目選択肢を組み立てる
 * select系（single_select / multiple_select）はプルダウン選択肢として展開せず、
 * まずはテキスト入力でオプション表示名（value）を直接入力する運用とする。
 * そのため項目選択肢としてはカスタムフィールド単位で1エントリのみ生成する。
 */
export function buildCustomFieldOptions(customFields: SharedCustomField[]) {
  return customFields.map((field) => ({
    label: field.name,
    value: buildCustomFieldColumn(field.id),
  }));
}

/** condition_column がカスタムフィールド（"field_<id>"）かどうかを判定する */
export function isCustomFieldColumn(column: string): boolean {
  return /^field_\d+$/.test(column);
}

/** condition_column からカスタムフィールドIDを取り出す（該当しない場合は null） */
export function extractCustomFieldId(column: string): number | null {
  const matched = column.match(/^field_(\d+)$/);
  if (!matched) return null;
  return Number(matched[1]);
}

/** このタグ条件UIで扱う演算子（バックエンドのフルセットのうち一部を使用） */
export type TagConditionOperator = 'eq' | 'incl' | 'incl_all' | 'excl';

export const OPERATOR_OPTIONS: { label: string; value: TagConditionOperator }[] = [
  { label: 'が一致する', value: 'eq' },
  { label: 'のいずれかを含む（OR）', value: 'incl' },
  { label: 'のすべてを含む（AND）', value: 'incl_all' },
  { label: 'を含まない', value: 'excl' },
];

const OPERATOR_VALUES = OPERATOR_OPTIONS.map((option) => option.value);

/**
 * 文字列がこのUIで扱う演算子として妥当かどうかを判定する型ガード。
 * as キャストを使わず、Select の onValueChange（string を返す）から
 * TagCondition['condition_type']（生成 union 型）へ安全に絞り込むために使う。
 */
export function isTagConditionOperator(value: string): value is TagConditionOperator {
  return (OPERATOR_VALUES as string[]).includes(value);
}
